package co.laiautomation

import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.laiautomation.downtyme.DowntymeRootComponent
import co.laiautomation.downtyme.DowntymeSchemaStartupState
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import com.laiautomation.kdxp.App as KdxpApp
import com.laiautomation.kdxp.KdxpBootstrapState
import com.laiautomation.kdxp.KdxpUserAccessState
import com.laiautomation.kdxp.sites.LiferaySite

/** Downtyme's shared Compose entry point. */
@Composable
fun App(root: DowntymeRootComponent) {
    val bootstrapState by root.bootstrapState.subscribeAsState()
    val userAccessState by root.userAccessState.subscribeAsState()
    val schemaStartupState by root.schemaStartupState.subscribeAsState()
    var retryGeneration by remember(root) { mutableIntStateOf(0) }

    val schemaProvisioningSite = bootstrapState.schemaProvisioningSiteOrNull(userAccessState)
    LaunchedEffect(
        root,
        schemaProvisioningSite?.id,
        userAccessState.loading,
        userAccessState.canAccessSetup,
        retryGeneration,
    ) {
        schemaProvisioningSite?.let { site ->
            root.provisionDowntymeSchema(
                site = site,
                force = retryGeneration > 0,
            )
        }
    }

    val readySite = (bootstrapState as? KdxpBootstrapState.Ready)?.site
    val schemaReadyForSelectedSite =
        (schemaStartupState as? DowntymeSchemaStartupState.Ready)?.siteId == readySite?.id
    val waitingForAdminSchemaProvisioning =
        readySite != null &&
            !userAccessState.loading &&
            userAccessState.canAccessSetup &&
            !schemaReadyForSelectedSite

    LaunchedEffect(
        root,
        readySite?.id,
        userAccessState.loading,
        userAccessState.canAccessSetup,
        schemaReadyForSelectedSite,
    ) {
        if (
            readySite != null &&
            !userAccessState.loading &&
            !waitingForAdminSchemaProvisioning
        ) {
            root.bindDowntymeCampaign(readySite)
        }
    }

    KdxpApp(root)

    val installing = schemaStartupState as? DowntymeSchemaStartupState.Installing
    if (installing != null) {
        AlertDialog(
            onDismissRequest = {},
            title = { Text("Preparing Downtyme") },
            text = {
                Text("Creating Downtyme's campaign data model.")
            },
            confirmButton = {},
        )
    }

    val failure = schemaStartupState as? DowntymeSchemaStartupState.Failed
    if (failure != null) {
        AlertDialog(
            onDismissRequest = {},
            title = { Text("Downtyme setup failed") },
            text = {
                Text(
                    "The campaign schema could not be initialized.\n\n${failure.message}"
                )
            },
            confirmButton = {
                TextButton(onClick = { retryGeneration += 1 }) {
                    Text("Retry")
                }
            },
        )
    }
}

private fun KdxpBootstrapState.schemaProvisioningSiteOrNull(
    userAccess: KdxpUserAccessState,
): LiferaySite? {
    if (userAccess.loading || !userAccess.canAccessSetup) return null

    return when (this) {
        is KdxpBootstrapState.SetupRequired ->
            takeIf { state -> state.schemaInstalled && state.canAccessSetup }?.site

        is KdxpBootstrapState.Ready -> site
        else -> null
    }
}
